import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { DinoActor, type FaunaSense } from '../../../dinos/DinoActor';
import { STEGO_KIT } from '../../../dinos/kits';
import type { Xyz } from '../../../interact/Interact';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';

export type SwampPoint = { x: number; y: number; z: number };

export type SwampHook = {
  dinos?: { id: string; x: number; y: number; z: number; hp: number; kind: string; taxon: string }[];
};

export type PoisonVolume = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

/** Dressing streams in once the player is west of the Crash Coast cliffs. */
export const SB_STREAM_X = -40;
/** Area slug flips past the cliff fringe, not on the face. */
export const SB_AREA_X = -50;

export const SB_CAMP: SwampPoint = { x: -78, y: 1, z: -24 };

export const SB_NODES: Record<string, SwampPoint> = {
  'SB-CAMP': SB_CAMP,
  'SB-BOARD': { x: -66, y: 0.22, z: -20 },
  'SB-POISON': { x: -62, y: 0.08, z: -12 },
  'SB-STEGO': { x: -54, y: 1, z: -34 },
};

/** Tea-water pool. Standing inside drains thirst faster. */
export const SB_POISON: PoisonVolume = { minX: -68, maxX: -56, minZ: -16, maxZ: -6 };
export const SB_POISON_MULT = 2.5;

export const SB_FOG_COLOR = 0x6a7050;
export const SB_FOG_DENSITY = 0.02;
export const SB_FOG_NIGHT = 0.03;

const GROUND_HALF_Y = 0.5;

type SwampMats = {
  peat: THREE.MeshStandardMaterial;
  murk: THREE.MeshStandardMaterial;
  water: THREE.MeshPhysicalMaterial;
  bone: THREE.MeshStandardMaterial;
  hide: THREE.MeshStandardMaterial;
  plank: THREE.MeshStandardMaterial;
  piling: THREE.MeshStandardMaterial;
  bark: THREE.MeshStandardMaterial;
  reed: THREE.MeshStandardMaterial;
  moss: THREE.MeshStandardMaterial;
  station: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): SwampMats {
  return {
    peat: new THREE.MeshStandardMaterial({
      color: 0x3a3a22,
      map: loadMap('/tex/sand.jpg', 6, 8),
      roughness: 0.98,
      metalness: 0.02,
    }),
    murk: new THREE.MeshStandardMaterial({
      color: 0x2a2814,
      map: loadMap('/tex/sand.jpg', 4, 4),
      roughness: 0.72,
      metalness: 0.04,
    }),
    hide: new THREE.MeshStandardMaterial({
      color: 0x6a5238,
      map: loadMap('/tex/hide.jpg', 1, 1),
      roughness: 0.9,
      metalness: 0.03,
    }),
    moss: new THREE.MeshStandardMaterial({
      color: 0x6a6a28,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.88,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.28,
    }),
    water: new THREE.MeshPhysicalMaterial({
      color: 0x3a4a18,
      roughness: 0.28,
      metalness: 0.04,
      transparent: true,
      opacity: 0.78,
      clearcoat: 0.18,
      clearcoatRoughness: 0.55,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    bone: new THREE.MeshStandardMaterial({
      color: 0xd4cbb4,
      roughness: 0.88,
      metalness: 0.04,
    }),
    plank: new THREE.MeshStandardMaterial({
      color: 0x5a4a32,
      map: loadMap('/tex/bark.jpg', 2, 1),
      roughness: 0.92,
      metalness: 0.03,
    }),
    piling: new THREE.MeshStandardMaterial({
      color: 0x3a3228,
      roughness: 0.94,
      metalness: 0.05,
    }),
    bark: new THREE.MeshStandardMaterial({
      color: 0x2e2820,
      map: loadMap('/tex/bark.jpg', 1, 3),
      roughness: 0.96,
      metalness: 0.02,
    }),
    reed: new THREE.MeshStandardMaterial({
      color: 0x6a6a38,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.86,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.3,
    }),
    station: new THREE.MeshStandardMaterial({
      color: 0x5a5848,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.94,
      metalness: 0.06,
    }),
    roof: new THREE.MeshStandardMaterial({
      color: 0x2a2824,
      roughness: 0.88,
      metalness: 0.1,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.72,
      metalness: 0.26,
    }),
  };
}

export function inPoisonVolume(x: number, z: number): boolean {
  return x > SB_POISON.minX && x < SB_POISON.maxX && z > SB_POISON.minZ && z < SB_POISON.maxZ;
}

/**
 * Swamp of Bones west of Crash Coast. Coast / jungle stay resident;
 * this dressing streams in at x < -40 and the area slug flips at x < -50.
 */
const STEGO_BOUNDS = { minX: -64, maxX: -44, minZ: -40, maxZ: -24 };

export class SwampOfBones {
  streamed = false;
  stego: DinoActor | null = null;
  private groundReady = false;

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Default slab ends at x ≈ -32. Cover the west cliffs through Camp Marrow.
    physics.addStaticCuboid(40, GROUND_HALF_Y, 26, -72, -GROUND_HALF_Y, -10);
    this.groundReady = true;
  }

  /** Load dressing the first time the player is west of the cliffs. */
  sync(scene: THREE.Scene, physics: PhysicsWorld, x: number): void {
    this.extendGround(physics);
    if (this.streamed || x >= SB_STREAM_X) {
      return;
    }
    this.build(scene, physics);
  }

  tick(dt: number, player: Xyz, sense?: FaunaSense): void {
    this.stego?.tick(dt, player, sense);
  }

  publish(hook: SwampHook): void {
    if (this.stego) {
      hook.dinos = [...(hook.dinos ?? []), this.stego.snap()];
    }
  }

  applyFog(scene: THREE.Scene, night: boolean): void {
    if (scene.fog instanceof THREE.FogExp2) {
      scene.fog.density = night ? SB_FOG_NIGHT : SB_FOG_DENSITY;
      scene.fog.color.setHex(SB_FOG_COLOR);
    }
    if (scene.background instanceof THREE.Color) {
      scene.background.setHex(night ? 0x2a3020 : SB_FOG_COLOR);
    }
  }

  private build(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'Swamp of Bones');
    root.userData.area = 'swamp';
    scene.add(root);

    const mats = makeMats();
    addPeat(root, mats);
    addMurk(root, mats);
    addTeaWater(root, mats);
    addBoardwalk(root, physics, mats);
    addCypress(root, physics, mats);
    addKnees(root, mats);
    addReeds(root, mats);
    addHangingMoss(root, mats);
    addBones(root, mats);
    addCamp(root, physics, mats);
    this.spawnStego(root);
  }

  private spawnStego(root: THREE.Group): void {
    const spot = SB_NODES['SB-STEGO'] ?? { x: -54, y: 1, z: -34 };
    this.stego = new DinoActor('SB-STEGO', STEGO_KIT, spot.x, spot.z, STEGO_BOUNDS);
    root.add(this.stego.mesh);
  }
}

function addPeat(root: THREE.Group, mats: SwampMats): void {
  const geo = new THREE.PlaneGeometry(82, 54, 20, 14);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) - 74;
      const z = pos.getZ(i) - 12;
      let y = Math.sin(x * 0.11 + z * 0.09) * 0.06 + Math.sin(z * 0.22) * 0.03;
      if (Math.hypot(x + 70, z + 20) < 4 || Math.hypot(x + 66, z + 20) < 8 || Math.hypot(x + 78, z + 24) < 4) {
        y = 0;
      }
      if (x > -68 && x < -56 && z > -16 && z < -6) {
        y = -0.03;
      }
      pos.setY(i, 0.016 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const floor = new THREE.Mesh(geo, mats.peat);
  floor.position.set(-74, 0, -12);
  floor.receiveShadow = true;
  tag(floor, 'SB-PEAT');
  root.add(floor);
}

function addMurk(root: THREE.Group, mats: SwampMats): void {
  const puddles: [number, number, number][] = [
    [-54, -4, 3.4],
    [-88, -28, 4.1],
    [-96, -8, 3.2],
    [-50, -26, 2.8],
    [-74, 4, 3.6],
    [-84, -36, 3.0],
  ];
  for (let i = 0; i < puddles.length; i++) {
    const p = puddles[i];
    if (!p) {
      continue;
    }
    const [x, z, r] = p;
    const pad = new THREE.Mesh(new THREE.CircleGeometry(r, 14), mats.murk);
    pad.rotation.x = -Math.PI / 2;
    pad.position.set(x, 0.03, z);
    pad.receiveShadow = true;
    tag(pad, `SB-MURK-${String(i + 1).padStart(2, '0')}`);
    root.add(pad);
  }
}

function addTeaWater(root: THREE.Group, mats: SwampMats): void {
  const pos = SB_NODES['SB-POISON'] ?? { x: -62, y: 0.08, z: -12 };
  const pool = new THREE.Mesh(new THREE.CircleGeometry(7.4, 22), mats.water);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(pos.x, pos.y, pos.z);
  pool.renderOrder = 1;
  tag(pool, 'SB-POISON');
  root.add(pool);

  const sheet = new THREE.Mesh(new THREE.CircleGeometry(9.5, 20), mats.water);
  sheet.rotation.x = -Math.PI / 2;
  sheet.scale.set(1.6, 1, 0.7);
  sheet.position.set(-70, 0.07, -8);
  sheet.renderOrder = 1;
  tag(sheet, 'SB-TEA');
  root.add(sheet);

  const extra: [number, number, number][] = [
    [-78, -4, 4.2],
    [-54, -14, 3.6],
    [-84, -16, 3.8],
  ];
  for (let i = 0; i < extra.length; i++) {
    const e = extra[i];
    if (!e) {
      continue;
    }
    const lobe = new THREE.Mesh(new THREE.CircleGeometry(e[2], 14), mats.water);
    lobe.rotation.x = -Math.PI / 2;
    lobe.position.set(e[0], 0.075, e[1]);
    lobe.renderOrder = 1;
    tag(lobe, `SB-TEA-${String(i + 1).padStart(2, '0')}`);
    root.add(lobe);
  }
}

function addBoardwalk(root: THREE.Group, physics: PhysicsWorld, mats: SwampMats): void {
  const pos = SB_NODES['SB-BOARD'] ?? { x: -66, y: 0.22, z: -20 };
  const walk = new THREE.Group();
  tag(walk, 'SB-BOARD');
  walk.position.set(pos.x, 0, pos.z);
  root.add(walk);

  const deck = new THREE.Mesh(new THREE.BoxGeometry(14, 0.16, 1.6), mats.plank);
  deck.position.set(0, pos.y, 0);
  deck.castShadow = true;
  deck.receiveShadow = true;
  walk.add(deck);

  const posts: [number, number][] = [
    [-6.4, -0.7],
    [-6.4, 0.7],
    [0, -0.7],
    [0, 0.7],
    [6.4, -0.7],
    [6.4, 0.7],
  ];
  for (let i = 0; i < posts.length; i++) {
    const p = posts[i];
    if (!p) {
      continue;
    }
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.7, 6), mats.piling);
    post.position.set(p[0], 0.2, p[1]);
    post.castShadow = true;
    walk.add(post);
  }

  physics.addStaticCuboid(7, 0.1, 0.8, pos.x, pos.y, pos.z);
}

function addCypress(root: THREE.Group, physics: PhysicsWorld, mats: SwampMats): void {
  const trees: [number, number, number][] = [
    [-52, -6, 6.4],
    [-56, -28, 7.1],
    [-88, -4, 6.8],
    [-92, -32, 7.4],
    [-84, -14, 5.9],
    [-48, -18, 6.2],
    [-96, -18, 7.0],
    [-58, 4, 6.6],
    [-74, 6, 5.8],
    [-90, 8, 6.9],
    [-46, -8, 6.5],
    [-100, -28, 7.2],
    [-70, -38, 6.3],
    [-98, 2, 6.8],
  ];
  const count = trees.length;
  const trunks = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.12, 0.38, 1, 7), mats.bark, count);
  trunks.name = 'SB-CYPRESS';
  trunks.castShadow = true;
  trunks.receiveShadow = true;
  root.add(trunks);

  const dummy = new THREE.Object3D();
  for (let i = 0; i < count; i++) {
    const tree = trees[i];
    if (!tree) {
      continue;
    }
    const [x, z, h] = tree;
    dummy.position.set(x, h * 0.5, z);
    dummy.rotation.set(0.03, x * 0.11, -0.02);
    dummy.scale.set(1, h, 1);
    dummy.updateMatrix();
    trunks.setMatrixAt(i, dummy.matrix);
    physics.addStaticCylinder(h * 0.5, 0.32, x, h * 0.5, z);
  }
  trunks.instanceMatrix.needsUpdate = true;
  trunks.computeBoundingSphere();
}

function addKnees(root: THREE.Group, mats: SwampMats): void {
  const knees: [number, number][] = [
    [-51.4, -5.6],
    [-52.8, -7.2],
    [-55.4, -26.8],
    [-57.2, -29.4],
    [-86.6, -3.2],
    [-89.4, -5.4],
    [-91.2, -30.6],
    [-93.4, -33.4],
    [-83.2, -12.6],
    [-85.4, -15.2],
    [-47.4, -16.6],
    [-49.2, -19.4],
    [-95.2, -16.4],
    [-97.4, -19.6],
    [-57.2, 3.2],
    [-59.4, 5.4],
    [-73.2, 5.2],
    [-75.6, 7.4],
    [-89.2, 7.2],
    [-91.6, 9.4],
    [-45.2, -6.6],
    [-47.4, -9.4],
    [-99.2, -26.4],
    [-101.2, -29.2],
    [-69.2, -36.4],
    [-71.4, -39.2],
    [-97.2, 1.2],
    [-99.4, 3.4],
  ];
  for (let i = 0; i < knees.length; i++) {
    const k = knees[i];
    if (!k) {
      continue;
    }
    const knee = new THREE.Mesh(new THREE.ConeGeometry(0.2 + (i % 3) * 0.04, 0.5 + (i % 4) * 0.08, 5), mats.bark);
    knee.position.set(k[0], 0.22, k[1]);
    knee.castShadow = true;
    tag(knee, `SB-KNEE-${String(i + 1).padStart(2, '0')}`);
    root.add(knee);
  }
}

function addReeds(root: THREE.Group, mats: SwampMats): void {
  const clumps: [number, number][] = [
    [-54, -10],
    [-70, -4],
    [-86, -8],
    [-60, -30],
    [-80, -34],
    [-94, -22],
    [-48, -14],
    [-98, -14],
    [-72, 8],
    [-52, 2],
    [-90, -36],
    [-64, -38],
  ];
  const blade = new THREE.PlaneGeometry(0.35, 1.6);
  for (let i = 0; i < clumps.length; i++) {
    const c = clumps[i];
    if (!c) {
      continue;
    }
    const g = new THREE.Group();
    tag(g, `SB-REED-${String(i + 1).padStart(2, '0')}`);
    g.position.set(c[0], 0.7, c[1]);
    for (let k = 0; k < 5; k++) {
      const card = new THREE.Mesh(blade, mats.reed);
      card.position.set((k - 2) * 0.12, 0, (k % 2) * 0.08);
      card.rotation.y = k * 0.55 + i * 0.2;
      g.add(card);
    }
    root.add(g);
  }
}

function addHangingMoss(root: THREE.Group, mats: SwampMats): void {
  const cards: [number, number, number][] = [
    [-52, 3.4, -6],
    [-88, 3.6, -4],
    [-92, 3.8, -32],
    [-56, 3.5, -28],
    [-84, 3.2, -14],
    [-96, 3.7, -18],
    [-74, 3.1, 6],
    [-100, 3.4, -28],
  ];
  const sheet = new THREE.PlaneGeometry(1.4, 2.2);
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    if (!c) {
      continue;
    }
    const moss = new THREE.Mesh(sheet, mats.moss);
    moss.position.set(c[0], c[1], c[2]);
    moss.rotation.y = i * 0.4;
    tag(moss, `SB-MOSS-${String(i + 1).padStart(2, '0')}`);
    root.add(moss);
  }
}

function addBones(root: THREE.Group, mats: SwampMats): void {
  const piles: [number, number, number][] = [
    [-72, -28, 0.18],
    [-86, -6, 0.22],
    [-54, -22, 0.16],
    [-94, -26, 0.2],
    [-68, 2, 0.15],
    [-82, -32, 0.19],
    [-50, -8, 0.17],
    [-100, -10, 0.21],
    [-76, 8, 0.16],
    [-64, -36, 0.18],
    [-88, 4, 0.2],
    [-48, -30, 0.15],
  ];
  for (let i = 0; i < piles.length; i++) {
    const pile = piles[i];
    if (!pile) {
      continue;
    }
    const [x, z, y] = pile;
    const g = new THREE.Group();
    tag(g, `SB-BONES-${String(i + 1).padStart(2, '0')}`);
    g.position.set(x, y, z);
    root.add(g);

    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.05, 5, 10, Math.PI), mats.bone);
    rib.rotation.x = Math.PI / 2;
    rib.castShadow = true;
    g.add(rib);

    const rib2 = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.04, 5, 9, Math.PI), mats.bone);
    rib2.rotation.set(Math.PI / 2, 0.4, 0.3);
    rib2.position.set(0.12, 0.06, -0.08);
    rib2.castShadow = true;
    g.add(rib2);

    const long = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 0.95, 5), mats.bone);
    long.rotation.z = 1.05;
    long.position.set(0.2, 0.08, 0.1);
    long.castShadow = true;
    g.add(long);

    const long2 = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.8, 5), mats.bone);
    long2.rotation.set(0.4, 0, -0.9);
    long2.position.set(-0.22, 0.06, 0.16);
    long2.castShadow = true;
    g.add(long2);

    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), mats.bone);
    skull.scale.set(1, 0.75, 1.15);
    skull.position.set(-0.15, 0.14, -0.05);
    skull.castShadow = true;
    g.add(skull);

    const hide = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.45), mats.hide);
    hide.rotation.x = -Math.PI / 2 + 0.15;
    hide.position.set(0.05, 0.04, 0.18);
    g.add(hide);
  }
}

function addCamp(root: THREE.Group, physics: PhysicsWorld, mats: SwampMats): void {
  const camp = new THREE.Group();
  tag(camp, 'Camp Marrow');
  camp.position.set(SB_CAMP.x, 0, SB_CAMP.z);
  root.add(camp);

  const body = new THREE.Mesh(new THREE.BoxGeometry(4.2, 2.4, 3.4), mats.station);
  body.position.y = 1.2;
  body.castShadow = true;
  body.receiveShadow = true;
  tag(body, 'SB-CAMP');
  camp.add(body);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.14, 3.8), mats.roof);
  roof.position.y = 2.48;
  roof.castShadow = true;
  camp.add(roof);

  const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.24, 0.05), mats.rust);
  plaque.position.set(0, 1.9, 1.74);
  camp.add(plaque);

  const rack = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.08, 0.55), mats.plank);
  rack.position.set(1.8, 0.55, 0.4);
  camp.add(rack);
  const femur = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 1.2, 5), mats.bone);
  femur.rotation.z = 1.2;
  femur.position.set(1.8, 0.64, 0.4);
  camp.add(femur);

  physics.addStaticCuboid(2.1, 1.2, 1.7, SB_CAMP.x, 1.2, SB_CAMP.z);
  void dressSwampStation(camp, mats);
}

const STATION_GLB = '/models/bld/bld_swamp_station.glb';

/** Swap the box station for the authored Camp Marrow GLB. Keeps primitives if the file is missing. */
async function dressSwampStation(camp: THREE.Group, mats: SwampMats): Promise<void> {
  try {
    const hero = await loadGlb(STATION_GLB);
    hero.name = 'SB-CAMP-HERO';
    const rock = mats.station.map;
    const wreck = mats.rust.map;
    const fern = mats.moss.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && (slug.includes('concrete') || slug.includes('moss'))) {
          mat.map = slug.includes('moss') && fern ? fern : rock;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('roof') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...camp.children]) {
      child.visible = false;
    }
    camp.add(hero);
  } catch {
    /* keep box station */
  }
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
